import type {
  LinkInBioBlock,
  LinkInBioBlockType,
  LinkInBioPageSummary,
  LinkInBioPageType,
} from '@repo/lib';
import { getLinkInBioBlockSchedule, isLinkInBioEventOver } from '@repo/lib';

const scheduleDate = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
});

export const formatScheduleDate = (iso: string) => scheduleDate.format(new Date(iso));

export const BLOCK_TYPE_LABELS: Record<LinkInBioBlockType, string> = {
  page: 'Page',
  events: 'Upcoming events',
  link: 'Link',
  heading: 'Heading',
  portal: 'Customer portal',
  email: 'Email sign-up',
};

export const PAGE_TYPE_LABELS: Record<LinkInBioPageType, string> = {
  checkout: 'Checkout page',
  event: 'Event',
  form: 'Form',
};

export function blockKindLabel(block: LinkInBioBlock, page: LinkInBioPageSummary | undefined) {
  return block.type === 'page' && page
    ? PAGE_TYPE_LABELS[page.type]
    : BLOCK_TYPE_LABELS[block.type];
}

export function blockTitle(
  block: LinkInBioBlock,
  page: LinkInBioPageSummary | undefined,
  portalLabel: string,
) {
  switch (block.type) {
    case 'page':
      return block.title || page?.title || 'Page';
    case 'events':
      return `Upcoming events (up to ${block.limit ?? 5})`;
    case 'portal':
      return block.title || portalLabel;
    default:
      return block.title || BLOCK_TYPE_LABELS[block.type];
  }
}

export function blockHint(
  block: LinkInBioBlock,
  page: LinkInBioPageSummary | undefined,
  isLoadingPages: boolean,
  now: Date,
): string | null {
  if (block.hidden) return 'Hidden';
  const schedule = getLinkInBioBlockSchedule(block, now);
  if (schedule === 'scheduled') return `Shows from ${formatScheduleDate(block.showFrom!)}`;
  if (schedule === 'ended') return `Schedule ended ${formatScheduleDate(block.showUntil!)}`;
  if (block.type !== 'page' || !block.page) return null;
  if (!page) return isLoadingPages ? null : 'Deleted, not shown';
  if (page.status === 'draft') return 'Draft, not shown';
  if (page.status === 'archived') return 'Archived, not shown';
  if (page.testmode) return 'Test mode, not shown';
  if (page.type === 'event' && isLinkInBioEventOver(page.event, now)) return 'Ended, not shown';
  return null;
}

export function scheduleNote(block: LinkInBioBlock, now: Date): string | null {
  if (block.hidden || !block.showUntil) return null;
  return getLinkInBioBlockSchedule(block, now) === 'showing'
    ? `Until ${formatScheduleDate(block.showUntil)}`
    : null;
}

export function canShowAsCard(block: LinkInBioBlock) {
  return (
    block.type === 'page' ||
    block.type === 'events' ||
    (block.type === 'link' && !!block.thumbnail?.url)
  );
}
